import {firestore} from '../config/firebase';
import {getFirestore, collection, query, where, getDocs, addDoc, updateDoc, deleteDoc, doc, onSnapshot, serverTimestamp, writeBatch} from '@react-native-firebase/firestore';
import {fetchPointRoadGeometry, fetchRouteRoadGeometry} from './roadGeometry';

export interface RoutePoint {
  latitude: number;
  longitude: number;
  order: number; // Order in the route sequence
}

/**
 * Road geometry attached to a POINT closure at save time.
 * See src/services/roadGeometry.ts for how these are populated.
 *
 * All fields optional so existing closures (saved before this enrichment
 * shipped) continue to work — the polygon generator falls back to a
 * plain 30m × 30m square when they're missing.
 */
export interface PointRoadGeometry {
  /** Snapped lat/lng of the closure on the nearest road edge. */
  snappedLatitude: number;
  snappedLongitude: number;
  /** Bearing of the nearest road at the closure (0-360, degrees clockwise from north). */
  roadBearing: number;
  /**
   * True if the closure sits within ~15m of a real intersection
   * (any node with degree ≥ 3). When true, the polygon generator
   * places a 30m × 30m square at the intersection instead of a
   * skinny stop bar (so all approaches are blocked).
   */
  atIntersection: boolean;
}

/**
 * A single intersection along a ROUTE closure. Each cross street bearing
 * produces one skinny "stop bar" polygon perpendicular to that cross
 * street. See src/services/closurePolygons.ts.
 */
export interface RouteIntersection {
  latitude: number;
  longitude: number;
  /** Bearing of the closed road at this node (0-360). */
  closedRoadBearing: number;
  /**
   * Bearings of cross streets from this node (0-360). Continuation
   * of the closed road (i.e. the next matched edge in the trace) is
   * NOT included — those edges are the closure itself.
   */
  crossStreetBearings: number[];
}

/**
 * Endpoint of a ROUTE closure (start or end). The polygon builder places:
 *  - Always: a perpendicular stop bar across the closed road at this
 *    node, to block traffic from entering the closure along its own
 *    direction from the continuation of the same road past the endpoint.
 *  - If `isAtIntersection` is true: additionally, one stop bar per
 *    cross-street entry, same logic as interior intersections.
 */
export interface RouteEndpoint {
  latitude: number;
  longitude: number;
  /** Bearing of the closed road at this endpoint (0-360). */
  closedRoadBearing: number;
  /**
   * Cross-street bearings at this endpoint (0-360). Empty when the
   * endpoint is mid-block. Same continuation-filter rules as
   * RouteIntersection.crossStreetBearings.
   */
  crossStreetBearings: number[];
  /** True when this endpoint sits at a real intersection (has ≥ 1 cross street). */
  isAtIntersection: boolean;
}

/**
 * Road geometry attached to a ROUTE closure at save time.
 */
export interface RouteRoadGeometry {
  intersections: RouteIntersection[];
  startEndpoint: RouteEndpoint | null;
  endEndpoint: RouteEndpoint | null;
}

export interface RoadClosure {
  id: string;
  accountId: string;
  latitude: number; // For point closures, or start point for routes
  longitude: number; // For point closures, or start point for routes
  type: string; // Changed to string to support custom closure types
  status: 'closed' | 'open';
  // Per-pin routing impact (Phase 6). 'full' = routing avoids it (an exclude
  // polygon is generated); 'limited' = passable, informational only (no
  // polygon). Distinct from lifecycle `status` (active vs. cleared). Optional
  // for backward compatibility — pre-existing closures fall back to a
  // type-based default (see defaultImpactForType in closurePolygons.ts).
  impact?: 'full' | 'limited';
  description: string;
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
  // Route-specific fields (optional for backward compatibility)
  isRoute?: boolean; // True if this is a multi-point route (e.g., parade route)
  routePoints?: RoutePoint[]; // Array of points defining the route path
  snappedRouteCoordinates?: Array<{latitude: number; longitude: number}>; // Snapped route coordinates following roads
  encodedPolyline?: string; // Google Maps encoded polyline for efficient storage
  // Road geometry enrichment (populated at save time; optional for
  // backward compatibility with pre-enrichment closures).
  pointGeometry?: PointRoadGeometry; // Only for point closures (isRoute !== true)
  routeGeometry?: RouteRoadGeometry; // Only for route closures (isRoute === true)
}

/**
 * Append-only audit event for the administrator closure log (CSV export).
 *
 * One document is written to the `closureEvents` collection every time a road
 * is closed, reopened, or a closure is edited. This is REQUIRED because
 * reopening a road hard-deletes the closure document, so the closure record
 * itself cannot be used as a history. Events accumulate only from the moment
 * this logging ships — pre-existing opens/closes cannot be reconstructed.
 */
export interface ClosureEvent {
  id: string;
  accountId: string;
  closureId: string; // stable id → lets a close row pair with its open row
  action: 'closed' | 'opened' | 'edited';
  type: string; // closure type key
  impact: 'full' | 'limited'; // 'full' = closed to all traffic, 'limited' = still passable
  notes: string; // free-text description ("northbound open, flagger", etc.)
  latitude: number;
  longitude: number;
  street: string;
  crossStreet: string;
  isRoute: boolean;
  actorEmail: string; // who performed the action (close/open may differ)
  actorName: string;
  actorId: string;
  timestamp: Date;
  durationMinutes?: number; // on 'opened': minutes the road was closed
  changeSummary?: string; // on 'edited': human-readable summary of what changed
}

// Caller-supplied event payload (id + timestamp are assigned on write).
export type ClosureEventInput = Omit<ClosureEvent, 'id' | 'timestamp'>;

export const closuresApi = {
  // Fetch active (closed) closures for an account (one-time)
  async getClosures(accountId: string): Promise<RoadClosure[]> {
    try {
      const db = getFirestore();
      const q = query(collection(db, 'closures'), where('accountId', '==', accountId), where('status', '==', 'closed'));
      const snapshot = await getDocs(q);

      const closures = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date(),
      })) as RoadClosure[];

      // Sort by createdAt descending (newest first)
      return closures.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
    } catch (error) {
      console.error('Error fetching closures:', error);
      throw error;
    }
  },

  // Subscribe to real-time updates for closures
  subscribeToClosures(
    accountId: string,
    callback: (closures: RoadClosure[]) => void,
  ): () => void {
    console.log('🔔 Subscribing to closures for account:', accountId);
    const db = getFirestore();
    const q = query(collection(db, 'closures'), where('accountId', '==', accountId), where('status', '==', 'closed'));
    const unsubscribe = onSnapshot(
      q,
      snapshot => {
        const closures = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate() || new Date(),
          updatedAt: doc.data().updatedAt?.toDate() || new Date(),
        })) as RoadClosure[];

        // Sort by createdAt descending (newest first)
        const sorted = closures.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
        console.log(`✅ Received ${sorted.length} closures for account ${accountId}`);
        callback(sorted);
      },
      error => {
        console.error('❌ Error in closures subscription for account', accountId, ':', error);
        console.error('Error code:', error?.code);
        console.error('Error message:', error?.message);
        if (error?.code === 'firestore/permission-denied') {
          console.error('⚠️ Permission denied - check Firestore security rules');
        }
        // Still call callback with empty array so UI doesn't hang
        callback([]);
      },
    );

    return unsubscribe;
  },

  // Create a new closure.
  //
  // Best-effort enrichment: before writing to Firestore we ask
  // Valhalla (via the enrichClosureGeometry Cloud Function) for road
  // geometry — the closed road's bearing (point closures), and every
  // intersection + cross-street bearings along the closure polyline
  // (route closures). If the call fails or times out we save the
  // closure without the enrichment; src/services/closurePolygons.ts
  // falls back to today's polygon shapes for closures missing this
  // data, so pre-enrichment closures remain valid.
  //
  // The enrichment call is short (~200-800ms typical) and only adds
  // ~1 Stadia request per closure save. The map's real-time listener
  // fires on the write, so the small latency here doesn't affect
  // routing responsiveness.
  async createClosure(closure: Omit<RoadClosure, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      let pointGeometry: RoadClosure['pointGeometry'];
      let routeGeometry: RoadClosure['routeGeometry'];

      // Route closures need at least a two-point polyline for
      // /trace_attributes. Prefer snapped coordinates (road-accurate)
      // over the user's tapped waypoints.
      if (closure.isRoute) {
        const polyline =
          closure.snappedRouteCoordinates && closure.snappedRouteCoordinates.length >= 2
            ? closure.snappedRouteCoordinates
            : (closure.routePoints || [])
                .slice()
                .sort((a, b) => a.order - b.order)
                .map(p => ({latitude: p.latitude, longitude: p.longitude}));
        if (polyline.length >= 2) {
          try {
            const enriched = await fetchRouteRoadGeometry(polyline);
            if (enriched) routeGeometry = enriched;
          } catch (enrichErr) {
            // Never let enrichment failures block closure creation.
            console.warn('🛣️ Route enrichment failed (saving without geometry):', enrichErr);
          }
        }
      } else if (
        typeof closure.latitude === 'number' &&
        typeof closure.longitude === 'number'
      ) {
        try {
          const enriched = await fetchPointRoadGeometry(closure.latitude, closure.longitude);
          if (enriched) pointGeometry = enriched;
        } catch (enrichErr) {
          console.warn('🛣️ Point enrichment failed (saving without geometry):', enrichErr);
        }
      }

      const db = getFirestore();
      const docRef = await addDoc(collection(db, 'closures'), {
        ...closure,
        ...(pointGeometry && {pointGeometry}),
        ...(routeGeometry && {routeGeometry}),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return docRef.id;
    } catch (error) {
      console.error('Error creating closure:', error);
      throw error;
    }
  },

  // Update a closure
  async updateClosure(closureId: string, updates: Partial<RoadClosure>): Promise<void> {
    try {
      const db = getFirestore();
      await updateDoc(doc(db, 'closures', closureId), {
        ...updates,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error updating closure:', error);
      throw error;
    }
  },

  // Delete a closure
  async deleteClosure(closureId: string): Promise<void> {
    try {
      const db = getFirestore();
      await deleteDoc(doc(db, 'closures', closureId));
    } catch (error) {
      console.error('Error deleting closure:', error);
      throw error;
    }
  },

  // Open all closures for an account.
  // `actor` (optional) is used to attribute the bulk-open events in the
  // administrator closure log. Street/cross-street are left blank for batch
  // opens (we intentionally do not geocode every closure here).
  async openAllClosures(
    accountId: string,
    actor?: {email: string; name: string; id: string},
  ): Promise<number> {
    try {
      const db = getFirestore();
      // Get all closed closures for this account
      const q = query(
        collection(db, 'closures'),
        where('accountId', '==', accountId),
        where('status', '==', 'closed')
      );
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        return 0; // No closed closures to open
      }

      const docs = snapshot.docs.filter(d => d.data().type !== 'limited_access_route');

      if (docs.length === 0) {
        return 0;
      }

      const BATCH_SIZE = 500; // Firestore batch limit
      let totalUpdated = 0;

      // Process in batches of 500
      for (let i = 0; i < docs.length; i += BATCH_SIZE) {
        const batch = writeBatch(db);
        const batchDocs = docs.slice(i, i + BATCH_SIZE);
        
        batchDocs.forEach(docSnapshot => {
          batch.update(doc(db, 'closures', docSnapshot.id), {
            status: 'open',
            updatedAt: serverTimestamp(),
          });
        });

        await batch.commit();
        totalUpdated += batchDocs.length;

        // Audit log: one 'opened' event per closure (non-fatal, fire-and-forget).
        if (actor) {
          batchDocs.forEach(docSnapshot => {
            const data: any = docSnapshot.data();
            const createdAt: Date | undefined = data.createdAt?.toDate?.();
            const durationMinutes = createdAt
              ? Math.max(0, Math.round((Date.now() - createdAt.getTime()) / 60000))
              : undefined;
            closuresApi.logClosureEvent({
              accountId,
              closureId: docSnapshot.id,
              action: 'opened',
              type: data.type || '',
              impact: data.impact === 'limited' ? 'limited' : 'full',
              notes: typeof data.description === 'string' ? data.description : '',
              latitude: typeof data.latitude === 'number' ? data.latitude : 0,
              longitude: typeof data.longitude === 'number' ? data.longitude : 0,
              street: '',
              crossStreet: '',
              isRoute: data.isRoute === true,
              actorEmail: actor.email,
              actorName: actor.name,
              actorId: actor.id,
              ...(durationMinutes !== undefined && {durationMinutes}),
              changeSummary: 'bulk open (Open All)',
            });
          });
        }
      }

      return totalUpdated;
    } catch (error) {
      console.error('Error opening all closures:', error);
      throw error;
    }
  },

  // Write one append-only audit event to `closureEvents`. Never throws — a
  // logging failure must not break closure creation / opening / editing.
  async logClosureEvent(event: ClosureEventInput): Promise<void> {
    try {
      const db = getFirestore();
      const payload: any = {...event, timestamp: serverTimestamp()};
      // Firestore rejects `undefined` field values — strip them.
      Object.keys(payload).forEach(k => {
        if (payload[k] === undefined) delete payload[k];
      });
      await addDoc(collection(db, 'closureEvents'), payload);
    } catch (err) {
      console.warn('⚠️ Failed to log closure event (non-fatal):', err);
    }
  },

  // Fetch closure audit events for an account within [start, end] (inclusive).
  // Sorted client-side (ascending) so we don't need an orderBy — the web
  // Firestore shim does not export orderBy.
  async getClosureEvents(
    accountId: string,
    start: Date,
    end: Date,
  ): Promise<ClosureEvent[]> {
    const db = getFirestore();
    const q = query(
      collection(db, 'closureEvents'),
      where('accountId', '==', accountId),
      where('timestamp', '>=', start),
      where('timestamp', '<=', end),
    );
    const snapshot = await getDocs(q);
    const events: ClosureEvent[] = snapshot.docs.map(d => {
      const data: any = d.data();
      return {
        ...(data as ClosureEvent),
        id: d.id,
        timestamp: data.timestamp?.toDate?.() || new Date(),
      };
    });
    return events.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
  },
};
